import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCog, FolderKanban, ListChecks, ArrowRight, Flag } from "lucide-react";
import { api, formatApiError, taskHref } from "@/lib/api";
import { toast } from "sonner";

export default function Reassignments() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [managers, setManagers] = useState({}); // workspace_id -> [managers]
  const [sel, setSel] = useState({}); // project_id -> manager_id
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reassignments");
      setData(data);
      const wsIds = [...new Set((data.projects || []).map((p) => p.workspace_id))];
      const map = {};
      await Promise.all(wsIds.map(async (wid) => {
        try {
          const m = await api.get(`/workspaces/${wid}/members`);
          map[wid] = (m.data.managers || []).filter((u) => u.status === "active");
        } catch { map[wid] = []; }
      }));
      setManagers(map);
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const reassign = async (proj) => {
    const uid = sel[proj.id];
    if (!uid) { toast.error("Selecione um gerente."); return; }
    setBusy(proj.id);
    try {
      await api.post(`/projects/${proj.id}/reassign-manager`, { user_id: uid });
      toast.success("Gerente atribuído.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const projects = data?.projects || [];
  const tasks = data?.tasks || [];
  const sprints = data?.sprints || [];
  const empty = projects.length === 0 && tasks.length === 0 && sprints.length === 0;

  return (
    <div className="space-y-6" data-testid="reassignments-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Espaços interdepartamentais</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">Reatribuições pendentes</h1>
        <p className="text-slate-500 mt-1">Projetos e tarefas de E.I. sob responsabilidade temporária do administrador, aguardando um novo gerente elegível.</p>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {data === null ? (
        <div className="text-slate-500">Carregando...</div>
      ) : empty ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="reassignments-empty">
          <UserCog className="w-10 h-10 text-slate-300 mx-auto" strokeWidth={1.5} />
          <h3 className="font-heading text-lg font-semibold mt-3">Nenhuma pendência</h3>
          <p className="text-sm text-slate-500 mt-1">Não há reatribuições de gerente pendentes nos espaços interdepartamentais.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="reassign-projects">
              <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><FolderKanban className="w-5 h-5 text-slate-500" /> Projetos aguardando gerente ({projects.length})</h2>
              <ul className="divide-y divide-slate-100">
                {projects.map((p) => (
                  <li key={p.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`reassign-project-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <Link to={`/projetos/${p.id}`} className="text-sm font-medium text-slate-900 hover:underline truncate block">{p.name}</Link>
                      <div className="text-xs text-slate-500">{p.workspace_name} · <span className="text-amber-700">Responsável temporário: {p.temp_admin_name || "Administrador"}</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={sel[p.id] || ""} onChange={(e) => setSel({ ...sel, [p.id]: e.target.value })} data-testid={`reassign-select-${p.id}`}
                        className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
                        <option value="">Selecionar gerente do E.I...</option>
                        {(managers[p.workspace_id] || []).map((m) => <option key={m.id} value={m.id}>{m.name} — {m.email}</option>)}
                      </select>
                      <button onClick={() => reassign(p)} disabled={busy === p.id} data-testid={`reassign-confirm-${p.id}`}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50">
                        Atribuir <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tasks.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="reassign-tasks">
              <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><ListChecks className="w-5 h-5 text-slate-500" /> Tarefas sob responsável temporário ({tasks.length})</h2>
              <p className="text-xs text-slate-400 mb-3">Abra a tarefa e atribua um gerente elegível (participante do projeto) para encerrar a pendência.</p>
              <ul className="divide-y divide-slate-100">
                {tasks.map((t) => (
                  <li key={t.id} className="py-3 flex items-center gap-3" data-testid={`reassign-task-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <Link to={taskHref({ id: t.id, project_id: t.project_id })} className="text-sm font-medium text-slate-900 hover:underline truncate block">{t.title}</Link>
                      <div className="text-xs text-slate-500">{t.project_name} · {t.workspace_name} · ex-responsável: {t.prev_assignee_name || "—"}</div>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 shrink-0">Reatribuição pendente</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sprints.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="reassign-sprints">
              <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Flag className="w-5 h-5 text-slate-500" /> Sprints ativas sem gerente ({sprints.length})</h2>
              <p className="text-xs text-slate-400 mb-3">Indique um novo gerente no projeto correspondente para encerrar a pendência. A sprint permanece ativa e nenhuma tarefa é movida.</p>
              <ul className="divide-y divide-slate-100">
                {sprints.map((s) => (
                  <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`reassign-sprint-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <Link to={`/projetos/${s.project_id}/sprints`} className="text-sm font-medium text-slate-900 hover:underline truncate block">{s.name}</Link>
                      <div className="text-xs text-slate-500">{s.project_name} · {s.workspace_name}</div>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 shrink-0">Reatribuição pendente</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
